import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { IntervalSelect } from "../components/IntervalSelect";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Search,
  Plus,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building2,
  User,
  Edit,
  Trash2,
  Eye,
  Filter,
  FileText,
} from "lucide-react";
import {
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  getEmployeeById,
  seedEmployees,
} from "../api/moniter";
import { getManagerList } from "../redux/slices/employee";
import { User as UserType } from "../types";
import { useToast } from "../hooks/useToast";
import { useAccess } from "../contexts/AccessContext";
import { country } from "../_mock/country";
import { state } from "../_mock/state";
import { dispatch, useSelector } from "../redux/store";
import PersonAvatar from "../components/people/PersonAvatar";

// Built-in choices. Anything a user adds via "Add interval" is stored per
// branch and merged in by IntervalSelect, so this list stays static.
const MONITOR_INTERVALS = [15, 30];

export function ScreenShot() {
  const [employees, setEmployees] = useState<UserType[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState<UserType | null>(
    null
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [seedingEmployees, setSeedingEmployees] = useState(false);
  const { toast } = useToast();
  const { canManage } = useAccess();

  // Same gate as the Work Schedule page — this is the Work Monitor management
  // surface for screenshots. Access Management alone decides: the
  // "work-schedule" panel at "Manage" enables these controls, "View" doesn't.
  const canManageEmployees = canManage("work-schedule");
  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchQuery, departmentFilter, roleFilter]);

  const fetchEmployees = async () => {
    try {
      console.log("Fetching employees...");
      const response = await getEmployees();
      console.log(
        "Employees fetched successfully:",
        response.monitors?.length || 0
      );
      setEmployees(response.monitors || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to load employees",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterEmployees = () => {
    let filtered = employees;

    if (searchQuery) {
      filtered = filtered.filter(
        (emp) =>
          emp.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.employeeID?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (departmentFilter !== "all") {
      filtered = filtered.filter((emp) => emp.department === departmentFilter);
    }

    if (roleFilter !== "all") {
      filtered = filtered.filter((emp) => emp.role === roleFilter);
    }

    setFilteredEmployees(filtered);
  };

  const handleAddEmployee = async (employeeData: Partial<UserType>) => {
    try {
      console.log("Adding new employee:", employeeData);
      await createEmployee(employeeData);
      toast({
        title: "Success",
        description: "Employee added successfully",
      });
      setShowAddModal(false);
      fetchEmployees();
    } catch (error) {
      console.error("Error adding employee:", error);
      toast({
        title: "Error",
        description: "Employee already exist",
        //variant: "destructive",
      });
    }
  };

  const handleEditEmployee = async (employeeData: Partial<UserType>) => {
    if (!selectedEmployee) return;

    try {
      console.log("Updating employee:", selectedEmployee._id, employeeData);
      await updateEmployee(selectedEmployee.id, employeeData);
      toast({
        title: "Success",
        description: "Employee updated successfully",
      });
      setShowEditModal(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      console.error("Error updating employee:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update employee",
        variant: "destructive",
      });
    }
  };

  const handleDeleteEmployee = async (id: string) => {
    try {
      console.log("Deleting employee:", id);
      await deleteEmployee(id);
      toast({
        title: "Success",
        description: "Employee deleted successfully",
      });
      fetchEmployees();
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete employee",
        variant: "destructive",
      });
    }
  };

  const handleViewEmployee = async (id: string) => {
    try {
      console.log("Fetching employee details:", id);
      const response = await getEmployeeById(id);
      setSelectedEmployee(response.employee);
      setShowViewModal(true);
    } catch (error) {
      console.error("Error fetching employee details:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to load employee details",
        variant: "destructive",
      });
    }
  };

  const handleSeedEmployees = async () => {
    try {
      console.log("Seeding employee data...");
      setSeedingEmployees(true);
      const response = await seedEmployees();
      toast({
        title: "Success",
        description: `${response.message} Default password: ${response.default_password}`,
      });
      fetchEmployees(); // Refresh the employee list
    } catch (error) {
      console.error("Error seeding employees:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to seed employee data",
        variant: "destructive",
      });
    } finally {
      setSeedingEmployees(false);
    }
  };

  const departments = [...new Set(employees.map((emp) => emp.department))];
  const roles = [...new Set(employees.map((emp) => emp.role))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Screen Shot Summary
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your organization's workforce
          </p>
        </div>
        <div className="flex gap-3">
          {canManageEmployees && (
            <>
              <Button
                onClick={handleSeedEmployees}
                disabled={seedingEmployees}
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                {seedingEmployees ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Seeding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Seed Data
                  </>
                )}
              </Button>
              <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Schedule
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-6xl bg-white max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>New Schedule</DialogTitle>
                    <DialogDescription>
                      Fill in the details to track your employee work
                    </DialogDescription>
                  </DialogHeader>
                  <EmployeeForm onSubmit={handleAddEmployee} />
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select
              value={departmentFilter}
              onValueChange={setDepartmentFilter}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by department" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Roles</SelectItem>
                {roles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Employee Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEmployees.map((employee) => (
          <Card
            key={employee._id}
            className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <PersonAvatar
                  userId={employee.user_id}
                  employeeCode={employee.employeeID}
                  name={employee.name}
                  size={48}
                />
                <Badge
                  variant={employee.isActive ? "default" : "secondary"}
                  className={employee.isActive ? "bg-green-500" : ""}
                >
                  {employee.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
              <div>
                <CardTitle className="text-lg">{employee.name}</CardTitle>
                <CardDescription className="text-sm">
                  {employee.designation} • {employee.department}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="h-4 w-4" />
                {employee.email}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="h-4 w-4" />
                {employee.employeeID}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" />
                Start time {employee.startTime} And End time {employee.endTime}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="h-4 w-4" />
                Login Id {employee.loginID}
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewEmployee(employee._id)}
                  className="flex-1"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
                {canManageEmployees && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedEmployee(employee);
                        setShowEditModal(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteEmployee(employee.user_id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* View Employee Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-4xl bg-white">
          <DialogHeader>
            <DialogTitle>Employee Details</DialogTitle>
          </DialogHeader>
          {selectedEmployee && <EmployeeDetails employee={selectedEmployee} />}
        </DialogContent>
      </Dialog>

      {/* Edit Employee Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-6xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
            <DialogDescription>Update employee information.</DialogDescription>
          </DialogHeader>
          {selectedEmployee && (
            <EmployeeForm
              employee={selectedEmployee}
              onSubmit={handleEditEmployee}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmployeeForm({
  employee,
  onSubmit,
}: {
  employee?: UserType;
  onSubmit: (data: Partial<UserType>) => void;
}) {
  const [formData, setFormData] = useState({
    startTime: employee?.startTime || "",
    endTime: employee?.endTime || "",
    shift: employee?.shift || "Day",
    interval: employee?.interval || "15",
    keyboardInterval: employee?.keyboardInterval || "15",
    mouseInterval: employee?.mouseInterval || "15",
    // No hardcoded default: null = inherit the branch-wide Activity Settings
    // value. Only a value the admin explicitly picks becomes a per-employee
    // override, so untouched employees keep following the branch value.
    activityMinutes: employee?.activityMinutes ?? null,
    department_id: employee?.department_id || "",
    emptID: employee?.emptID || "",
    employeeID: employee?.employeeID || "",
    start: employee?.start || true,
    stop: employee?.stop || false,
    weeks: employee?.weeks || [],
    sunday: employee?.sunday || false,
    monday: employee?.monday || true,
    tuesday: employee?.tuesday || true,
    wednesday: employee?.wednesday || true,
    thursday: employee?.thursday || true,
    friday: employee?.friday || true,
    saturday: employee?.saturday || true,
    datatype: "screenshot",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  useEffect(() => {
    if (formData.role !== "manager" && formData.department_id) {
      dispatch(getManagerList());
    }
  }, [formData.role, formData.department_id]);

  const managerList = useSelector((state) => state.employee.managerList);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid lg:grid-cols-4 sm:grid-cols-1 gap-4">
        <div>
          <Label htmlFor="startTime">Start Time</Label>
          <Input
            type="time"
            id="startTime"
            value={formData.startTime}
            onChange={(e) =>
              setFormData({ ...formData, startTime: e.target.value })
            }
            required
          />
        </div>
        <div>
          <Label htmlFor="endTime">End Time</Label>
          <Input
            type="time"
            id="endTime"
            value={formData.endTime}
            onChange={(e) =>
              setFormData({ ...formData, endTime: e.target.value })
            }
          />
        </div>

        {/* Work Shift dropdown removed — shift defaults to "Day"; overtime is
            now controlled via the manual clock-in/out button, not this field. */}

        <div>
          {/* <Label htmlFor="department_id">Interval</Label> */}
          <Label htmlFor="department_id">Screenshot Interval</Label>
          <IntervalSelect
            value={formData.interval}
            onChange={(value) =>
              setFormData({ ...formData, interval: value })
            }
            defaults={MONITOR_INTERVALS}
          />
        </div>

        <div>
          <Label htmlFor="role">Mouse Interval</Label>
          <IntervalSelect
            value={formData.mouseInterval}
            onChange={(value) =>
              setFormData({ ...formData, mouseInterval: value })
            }
            defaults={MONITOR_INTERVALS}
          />
        </div>

        <div>
          {/* <Label htmlFor="role">Keyboard Interval</Label> */}
          <Label htmlFor="role">Idle Time</Label>
          <IntervalSelect
            value={formData.keyboardInterval}
            onChange={(value) =>
              setFormData({ ...formData, keyboardInterval: value })
            }
            defaults={MONITOR_INTERVALS}
          />
        </div>

        <div>
          {/* Per-employee sync/upload interval — overrides the branch-wide
              Activity Settings default. */}
          <Label htmlFor="activityMinutes">Activity Interval</Label>
          <IntervalSelect
            value={formData.activityMinutes}
            onChange={(value) =>
              setFormData({ ...formData, activityMinutes: value })
            }
            defaults={MONITOR_INTERVALS}
          />
        </div>

        <div>
          <Label htmlFor="employeeID">Employee ID</Label>
          <Input
            id="employeeID"
            value={formData.employeeID}
            disabled={formData.emptID}
            onChange={(e) =>
              setFormData({ ...formData, employeeID: e.target.value })
            }
          />
        </div>
        <div>
          <Label htmlFor="department_id">Department</Label>
          <Select
            value={formData.department_id}
            disabled={formData.employeeID}
            onValueChange={(value) =>
              setFormData({ ...formData, department_id: value })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select department" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="Engineering">Engineering</SelectItem>
              <SelectItem value="Human Resources">Human Resources</SelectItem>
              <SelectItem value="Finance">Finance</SelectItem>
              <SelectItem value="Marketing">Marketing</SelectItem>
              <SelectItem value="Sales">Sales</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="role">Select Manager</Label>
          <Select
            value={formData.emptID}
            disabled={formData.employeeID}
            onValueChange={(value) =>
              setFormData({ ...formData, emptID: value })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Manager" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {managerList.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* {formData.role !== "manager" && formData.department_id && (
          <div>
            <Label htmlFor="role">Select Reporting Person</Label>
            <Select
              value={formData.manager_id}
              onValueChange={(value) =>
                setFormData({ ...formData, manager_id: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Reporting Person" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {managerList.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label htmlFor="designation">Designation</Label>
          <Input
            id="designation"
            value={formData.designation}
            onChange={(e) =>
              setFormData({ ...formData, designation: e.target.value })
            }
            required
          />
        </div> */}
      </div>
      <h1>Weeks</h1>
      <div className="grid lg:grid-cols-4 sm:grid-cols-1 gap-4">
        <div className="flex items-center space-x-2 mt-2">
          <Checkbox
            id="sameAs"
            checked={formData.monday}
            onCheckedChange={(checked) => {
              if (checked) {
                setFormData({
                  ...formData,
                  monday: true,
                });
              } else {
                setFormData({
                  ...formData,
                  monday: false,
                });
              }
            }}
          />
          <Label
            htmlFor="sameAsCurrent"
            className="text-sm font-medium leading-none"
          >
            Monday
          </Label>
          <Checkbox
            id="sameAs"
            checked={formData.tuesday}
            onCheckedChange={(checked) => {
              if (checked) {
                setFormData({
                  ...formData,
                  tuesday: true,
                });
              } else {
                setFormData({
                  ...formData,
                  tuesday: false,
                });
              }
            }}
          />
          <Label
            htmlFor="sameAsCurrent"
            className="text-sm font-medium leading-none"
          >
            Tuesday
          </Label>
          <Checkbox
            id="sameAs"
            checked={formData.wednesday}
            onCheckedChange={(checked) => {
              if (checked) {
                setFormData({
                  ...formData,
                  wednesday: true,
                });
              } else {
                setFormData({
                  ...formData,
                  wednesday: false,
                });
              }
            }}
          />
          <Label
            htmlFor="sameAsCurrent"
            className="text-sm font-medium leading-none"
          >
            Wednesday
          </Label>
          <Checkbox
            id="sameAs"
            checked={formData.thursday}
            onCheckedChange={(checked) => {
              if (checked) {
                setFormData({
                  ...formData,
                  thursday: true,
                });
              } else {
                setFormData({
                  ...formData,
                  thursday: false,
                });
              }
            }}
          />
          <Label
            htmlFor="sameAsCurrent"
            className="text-sm font-medium leading-none"
          >
            Thursday
          </Label>
          <Checkbox
            id="sameAs"
            checked={formData.friday}
            onCheckedChange={(checked) => {
              if (checked) {
                setFormData({
                  ...formData,
                  friday: true,
                });
              } else {
                setFormData({
                  ...formData,
                  friday: false,
                });
              }
            }}
          />
          <Label
            htmlFor="sameAsCurrent"
            className="text-sm font-medium leading-none"
          >
            Friday
          </Label>
          <Checkbox
            id="sameAs"
            checked={formData.saturday}
            onCheckedChange={(checked) => {
              if (checked) {
                setFormData({
                  ...formData,
                  saturday: true,
                });
              } else {
                setFormData({
                  ...formData,
                  saturday: false,
                });
              }
            }}
          />
          <Label
            htmlFor="sameAsCurrent"
            className="text-sm font-medium leading-none"
          >
            Saturday
          </Label>
          <Checkbox
            id="sameAs"
            checked={formData.sunday}
            onCheckedChange={(checked) => {
              if (checked) {
                setFormData({
                  ...formData,
                  sunday: true,
                });
              } else {
                setFormData({
                  ...formData,
                  sunday: false,
                });
              }
            }}
          />
          <Label
            htmlFor="sameAsCurrent"
            className="text-sm font-medium leading-none"
          >
            Sunday
          </Label>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          className="bg-gradient-to-r from-blue-500 to-purple-600"
        >
          {employee ? "Update" : "Start"} Schedule
        </Button>
      </div>
    </form>
  );
}

function EmployeeDetails({ employee }: { employee: UserType }) {
  return (
    <Tabs defaultValue="personal" className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="personal">Personal Info</TabsTrigger>
        <TabsTrigger value="employment">Employment</TabsTrigger>
        <TabsTrigger value="documents">Documents</TabsTrigger>
      </TabsList>
      <TabsContent value="personal" className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Full Name</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.name}</div>
          </div>
          <div>
            <Label>Email</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.email}</div>
          </div>
          <div>
            <Label>Phone</Label>
            <div className="p-2 bg-gray-50 rounded">
              {employee.phone || "N/A"}
            </div>
          </div>
          <div>
            <Label>Employee ID</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.employeeId}</div>
          </div>
        </div>
        {employee.address && (
          <div>
            <Label>Address</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.address}</div>
          </div>
        )}
        {employee.emergencyContact && (
          <div>
            <Label>Emergency Contact</Label>
            <div className="p-2 bg-gray-50 rounded">
              {employee.emergencyContact.name} (
              {employee.emergencyContact.relationship}) -{" "}
              {employee.emergencyContact.phone}
            </div>
          </div>
        )}
      </TabsContent>
      <TabsContent value="employment" className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Department</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.department}</div>
          </div>
          <div>
            <Label>Designation</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.designation}</div>
          </div>
          <div>
            <Label>Role</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.role}</div>
          </div>
          <div>
            <Label>Date of Joining</Label>
            <div className="p-2 bg-gray-50 rounded">
              jj
              {new Date(employee.dateOfJoining).toLocaleDateString()}
            </div>
          </div>
          <div>
            <Label>Status</Label>
            <div className="p-2 bg-gray-50 rounded">
              <Badge variant={employee.isActive ? "default" : "secondary"}>
                {employee.isActive ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>
        </div>
      </TabsContent>
      <TabsContent value="documents">
        <div className="text-center py-8 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No documents uploaded yet</p>
        </div>
      </TabsContent>
    </Tabs>
  );
}
